import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import PaidIcon from '@mui/icons-material/TaskAlt';
import SumIcon from '@mui/icons-material/Functions';
import AdvanceIcon from '@mui/icons-material/Savings';
import InstallmentIcon from '@mui/icons-material/ReceiptLong';
import RemainingIcon from '@mui/icons-material/HourglassBottom';
import StudentIcon from '@mui/icons-material/Group';
import { apiUrl, fetchJson } from '../../apiBase';

function StatCard({ title, value, loading, tone='neutral', icon }) {
  const paletteMap = (theme) => {
    const base = {
      primary: { bg: alpha(theme.palette.primary.light, 0.25), fg: theme.palette.primary.dark },
      success: { bg: alpha(theme.palette.success.light, 0.28), fg: theme.palette.success.dark },
      warning: { bg: alpha(theme.palette.warning.light, 0.28), fg: theme.palette.warning.dark },
      info: { bg: alpha(theme.palette.info.light, 0.25), fg: theme.palette.info.dark },
      neutral: { bg: alpha(theme.palette.grey[200], 0.6), fg: theme.palette.text.primary },
      secondary: { bg: alpha(theme.palette.secondary.light, 0.25), fg: theme.palette.secondary.dark },
    };
    return base[tone] || base.neutral;
  };
  return (
    <Card sx={(theme)=> ({
      height: '100%',
      borderRadius: 3,
      position:'relative',
      overflow:'hidden',
      background: `linear-gradient(135deg, ${paletteMap(theme).bg}, ${alpha(paletteMap(theme).bg,0.55)})`,
      boxShadow:'0 2px 6px rgba(0,0,0,0.04)',
      backdropFilter:'blur(2px)',
    })}>
      <CardContent sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Box sx={(theme)=> ({ width:36, height:36, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', background: alpha(paletteMap(theme).fg,0.12), color: paletteMap(theme).fg })}>
            {icon}
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight:600, color:'text.secondary' }}>{title}</Typography>
        </Box>
        {loading ? (
          <Skeleton variant="text" width={110} height={36} />
        ) : (
          <Typography variant="h5" sx={(theme)=> ({ fontWeight:700, color: paletteMap(theme).fg })}>{value}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function PaymentsStatistics({ schoolId, onError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      try {
        // Unified token retrieval (matches PaymentsManager)
        const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token || localStorage.getItem('kk_admin_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const base = apiUrl(`/admin/payments/${encodeURIComponent(schoolId)}/aggregate`);
  const res = await fetchJson(base, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) {
          const msg = 'Ödeme istatistikleri alınamadı';
          setError(msg); onError?.(msg);
        }
      } finally { if (!cancelled) setLoading(false); }
    }
    if (schoolId) load();
    return () => { cancelled = true; };
  }, [schoolId, onError]);

  const fmt = (n) => typeof n === 'number' ? new Intl.NumberFormat('tr-TR', { style:'currency', currency:'TRY', minimumFractionDigits:2 }).format(n) : '-';

  const [showAmounts, setShowAmounts] = useState(false);
  const mask = (formatted) => showAmounts ? formatted : (formatted ? '***' : '***');
  const totalPlan = mask(fmt(data?.total_plan_amount));
  const paid = mask(fmt(data?.total_paid_with_advance));
  const remaining = mask(fmt(data?.remaining_amount));
  const advance = mask(fmt(data?.total_advance_payment));
  const paidInstallments = mask(fmt(data?.total_paid_installments));

  return (
    <Box>
      <Box sx={{ mb: 3, display:'flex', flexWrap:'wrap', alignItems:'center', gap:2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight:700, mb:1 }}>Ödeme İstatistikleri</Typography>
          <Typography variant="body2" color="text.secondary">Okul genelindeki ödeme planı ve gerçekleşen ödemelerin özeti.</Typography>
        </Box>
        <Box sx={{ flex:1 }} />
        <Button size="small" variant="outlined" onClick={()=> setShowAmounts(s=> !s)}>{showAmounts? 'Tutarları Gizle':'Tutarları Göster'}</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={3}><StatCard title="Toplam Plan Tutarı" value={totalPlan} loading={loading} tone="primary" icon={<SumIcon fontSize="small" />} /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><StatCard title="Peşinat Toplamı" value={advance} loading={loading} tone="info" icon={<AdvanceIcon fontSize="small" />} /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><StatCard title="Taksit Ödemeleri" value={paidInstallments} loading={loading} tone="secondary" icon={<InstallmentIcon fontSize="small" />} /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><StatCard title="Ödenen (Peşinat + Taksit)" value={paid} loading={loading} tone="success" icon={<PaidIcon fontSize="small" />} /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><StatCard title="Kalan" value={remaining} loading={loading} tone="warning" icon={<RemainingIcon fontSize="small" />} /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><StatCard title="Planlı Öğrenci Sayısı" value={loading? '-' : data?.student_count_with_plan || 0} loading={loading} tone="neutral" icon={<StudentIcon fontSize="small" />} /></Grid>
      </Grid>
    </Box>
  );
}
